import { useMemo, useRef } from "react";

import { Option, Options, Value } from "../types";
import { flattenOptions } from "./utils";

type ActionMeta = {
  action: string;
  option: Option;
  removedValue: any;
};

export const SelectAllOption: Option = {
  value: "SELECT_ALL",
  label: "Select all"
};

export default function useSelectAll(
  options: Options = [],
  value: Value = [],
  onChange: (value: Value, actionMeta?: any) => void,
  fixedValues: string[]
) {
  const optionsWithSelectAll = [SelectAllOption, ...options];
  const valueRef = useRef(value);
  valueRef.current = value || [];

  const flattenedOptions = useMemo(() => flattenOptions(options), [options]);

  const onChangeWithSelectAll = (newValue: Value, actionMeta: ActionMeta) => {
    const { action, option, removedValue } = actionMeta;
    const item = option || removedValue;

    if (item && item.value === SelectAllOption.value) {
      if (action === "select-option") {
        onChange(flattenedOptions, actionMeta);
      } else if (action === "deselect-option") {
        const fixedOptions = flattenedOptions.filter(o =>
          fixedValues.includes(o.value)
        );
        onChange(fixedOptions, actionMeta);
      }
    } else {
      if (action === "deselect-option" || action === "remove-value") {
        if (fixedValues.includes(item.value)) {
          return;
        }
      }
      onChange(newValue, actionMeta);
    }
  };

  const isAllSelected = () =>
    valueRef.current.length === flattenedOptions.length;

  const isOptionSelected = (option: Option) =>
    isAllSelected() ||
    valueRef.current.some(({ value }) => value === option.value);

  const valuesWithSelectAll = isAllSelected() ? flattenedOptions : value;

  return {
    optionsWithSelectAll,
    valuesWithSelectAll,
    isOptionSelected,
    onChangeWithSelectAll
  };
}
